import { createStrictContext } from 'utils/createContext'
import { IFITrackerContext, IdentifiedFieldIssue } from './types'
import {
  useEditIdentifiedFieldIssueMutation,
  useGetEjLogMetadataQuery,
  useIfiTrackerQuery,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'

const { Provider, useContextFn } =
  createStrictContext<IFITrackerContext>('IFITrackerContext')

export const useIFITrackerContext = useContextFn

const IFITrackerProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { data, loading, refetch } = useIfiTrackerQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      identifiedFieldIssueListInput: {
        projectId: { equals: projectId },
      },
      projectId: projectId!,
    },
  })
  const ejMetadata = useGetEjLogMetadataQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: { globalId: projectId! },
  })
  const [edit] = useEditIdentifiedFieldIssueMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editIdentifiedFieldIssue: {
        identifiedFieldIssue: { reportName, reportId },
      },
    }) => simpleEditNotification(reportName ?? reportId),
  })
  const issues = useOptions({
    items: data?.issues?.records,
    label: 'formattedNumber',
    value: 'id',
    sort: 'formattedNumber',
    tooltip: 'description',
  })
  const judgements = useOptions({
    items: data?.engineeringJudgements?.records,
    label: 'formattedNumber',
    value: 'id',
    sort: 'formattedNumber',
  })
  const ejTypesOptions = useOptions({
    items: ejMetadata?.data?.project?.ejTypes,
    label: 'name',
    value: 'id',
    sort: 'name',
  })
  return (
    <Provider
      value={{
        loading,
        refetch,
        identifiedFieldIssues: data?.identifiedFieldIssues?.records ?? [],
        sync: data?.identifiedFieldIssueSyncs?.[0],
        edit,
        review: useCIMSModalValues<IdentifiedFieldIssue>(),
        revert: useCIMSModalValues<IdentifiedFieldIssue>(),
        quickLinkOptions: {
          loading,
          quickLinks: data?.issueQuickLinks?.records ?? [],
        },
        issues,
        judgements,
        ejTypesOptions,
        ejMetadata: {
          ...ejMetadata,
          ...(ejMetadata?.data?.project ?? { ejQuickLinks: [], ejTypes: [] }),
        },
      }}
    >
      {children}
    </Provider>
  )
}

export default IFITrackerProvider
